/**
 * Capacite de bus **`credentials.audit`** — KLAVOX est le PRODUCTEUR.
 *
 * Contrat de socle KLAVOX <-> NAVKRYPT (Master_1, 2026-09-24,
 * `docs/ARCHITECTURE-USINE-RESILIENTE.md` § « CONTRAT DE SOCLE — audit de
 * fuites ») : le coffre s'audite LUI-MEME, le navigateur se contente
 * d'afficher. Le contrat evident — faire circuler
 * `{ origin, username, password }` vers NAVKRYPT — aurait detruit la
 * propriete zero-knowledge : un coffre qui exporte des mots de passe sur un
 * bus local n'est plus un coffre, c'est un fichier avec des etapes.
 *
 * Ce qui traverse le bus, et RIEN d'autre, par origine :
 *   `{ origin, compromis, faible, reutilise, verifie_le }`
 * Jamais un mot de passe, jamais un identifiant, jamais une empreinte (meme
 * partielle) : le rapprochement k-anonymat se fait entierement ici
 * (`breach-check.ts`), et seul le verdict booleen en sort.
 *
 * Les quatre regles du contrat, et ou elles vivent :
 *   1. source absente/en panne => « non verifie », JAMAIS « aucune fuite ».
 *      Porte par `verifie_le`, qui vaut `null` tant que rien n'a pu etre
 *      verifie (`compromis: false` se lit alors « on ne sait pas »).
 *   2. consentement explicite AVANT le premier audit, cote KLAVOX : sans lui
 *      la reponse est `consentement-requis`, jamais des verdicts.
 *   3. KLAVOX reste utilisable sans le bus (ce module est un repondeur pur,
 *      rien ne l'appelle si personne ne demande) et NAVKRYPT sans KLAVOX
 *      (`coffre-non-connecte` est un etat, pas une erreur).
 *   4. le choix de la source de fuites EU ne depend pas de ce module : il lit
 *      ce que `breach-check.ts` a obtenu, quel que soit le point d'acces.
 *
 * Ce module ne fait QUE repondre : il ne declenche aucun audit, n'ouvre
 * aucune connexion et ne persiste rien. `serveCredentialsAudit` est le chemin
 * qui AGIT — c'est lui, et pas une copie de la logique, que le garde-fou
 * anti-fuite exerce.
 */
import { analyzePasswordHygiene, entryUrisMatchingSite, type VaultItem } from '@klavox/vault-model';
import { extractDomain } from '../ui/render-utils.js';
import { accountPasswordWords } from './vault-health.js';

export const CREDENTIALS_AUDIT_CAPABILITY = 'credentials.audit';

/** Verdict par origine : que des booleens et une date. Aucun identifiant. */
export interface CredentialsAuditVerdict {
  readonly origin: string;
  readonly compromis: boolean;
  readonly faible: boolean;
  readonly reutilise: boolean;
  /** ISO8601 de la derniere verification des fuites ; `null` = non verifie. */
  readonly verifie_le: string | null;
}

export type CredentialsAuditResponse =
  | { readonly capacite: typeof CREDENTIALS_AUDIT_CAPABILITY; readonly etat: 'coffre-non-connecte' }
  | { readonly capacite: typeof CREDENTIALS_AUDIT_CAPABILITY; readonly etat: 'consentement-requis' }
  | {
      readonly capacite: typeof CREDENTIALS_AUDIT_CAPABILITY;
      readonly etat: 'ok';
      readonly verdicts: readonly CredentialsAuditVerdict[];
    };

/** Etat du coffre au moment de la demande — fourni par l'UI, jamais persiste ici. */
export interface CredentialsAuditSource {
  /** Coffre deverrouille sur cet appareil. */
  readonly deverrouille: boolean;
  /** Consentement explicite de l'utilisateur a l'affichage par un tiers. */
  readonly consenti: boolean;
  readonly entrees: readonly VaultItem[];
  /** Resultat de `checkPwnedPasswords` ; `null` = source absente ou en panne. */
  readonly breachCounts: ReadonlyMap<string, number> | null;
  /** Horodatage de ce resultat ; `null` = jamais verifie. */
  readonly breachVerifieLe: number | null;
  /**
   * Detection par defaut du coffre (reglage de l'appareil) pour les adresses sans regle propre ; absente = domaine.
   * Relue par `defaultUriMatchRule` : une valeur trafiquee ne peut que restreindre, jamais elargir.
   */
  readonly detectionParDefaut?: string;
  /**
   * E-mail du compte Klavox ouvert ; absent = aucun. Un mot de passe qui le reprend est « faible » sur toute entree,
   * comme dans la Sante du coffre (`accountPasswordWords`) : le verdict publie ne contredit pas ce que montre le coffre.
   * Lu ici, jamais publie : le garde-fou anti-fuite le compte parmi les valeurs interdites de bus.
   */
  readonly emailCompte?: string;
}

/** Demande entrante : un consommateur peut se limiter aux origines qu'il affiche. */
export interface CredentialsAuditRequest {
  readonly origines?: readonly string[];
}

export class CredentialsAuditLeakError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CredentialsAuditLeakError';
  }
}

function hostsOf(uris: readonly string[]): string[] {
  const origins = new Set<string>();
  for (const uri of uris) {
    const host = extractDomain(uri).toLowerCase();
    if (host !== '') origins.add(host);
  }
  return [...origins];
}

function originsOf(entry: VaultItem): string[] {
  return hostsOf(entry.secrets.uris ?? []);
}

/**
 * Agrege les verdicts par origine a partir des entrees DEJA DECHIFFREES en
 * memoire. Une origine porte le pire cas de ses entrees (un seul mot de passe
 * fuite sur `example.com` suffit a signaler `example.com`). Les entrees sans
 * URL ne produisent aucune origine : il n'y a rien a afficher pour elles.
 */
export function buildCredentialsAudit(source: CredentialsAuditSource): CredentialsAuditVerdict[] {
  const actives = source.entrees.filter((entry) => entry.deletedAt === null);
  const byId = new Map(actives.map((entry) => [entry.id, entry]));
  const verifieLe =
    source.breachCounts === null || source.breachVerifieLe === null
      ? null
      : new Date(source.breachVerifieLe).toISOString();

  const byOrigin = new Map<string, { compromis: boolean; faible: boolean; reutilise: boolean }>();
  for (const finding of analyzePasswordHygiene(actives, accountPasswordWords(source.emailCompte ?? '')).findings) {
    const entry = byId.get(finding.itemId)!;
    const compromis = (source.breachCounts?.get(entry.secrets.password ?? '') ?? 0) > 0;
    for (const origin of originsOf(entry)) {
      const current = byOrigin.get(origin) ?? { compromis: false, faible: false, reutilise: false };
      byOrigin.set(origin, {
        compromis: current.compromis || compromis,
        faible: current.faible || finding.weak,
        reutilise: current.reutilise || finding.reused,
      });
    }
  }

  return [...byOrigin.entries()]
    .map(([origin, flags]) => ({ origin, ...flags, verifie_le: verifieLe }))
    .sort((a, b) => a.origin.localeCompare(b.origin));
}

/** Toute valeur chiffree d'une entree — donc interdite de bus. Les URL n'en sont
 *  pas : l'origine est precisement ce que le contrat publie. */
function secretStrings(entry: VaultItem): string[] {
  const secrets = entry.secrets;
  const valeurs: unknown[] = [
    secrets.name,
    secrets.username,
    secrets.password,
    secrets.notes,
    secrets.totpSecret,
    ...(secrets.customFields ?? []).flatMap((field) => [field.name, field.value]),
  ];
  return valeurs.filter((value): value is string => typeof value === 'string' && value.length > 0);
}

/**
 * GARDE-FOU DUR : rien de secret ne sort d'ici. On relit la reponse telle
 * qu'elle partira sur le bus (serialisee) et on refuse de la rendre si une
 * valeur secrete du coffre y apparait. Un verdict est un booleen : si un
 * secret s'y est glisse, c'est un bug de construction, et il ne doit pas
 * partir. Une valeur egale a une origine TIREE DES URL DU COFFRE est ignoree
 * (un identifiant qui vaut exactement `example.com` n'est pas une fuite :
 * l'origine est, par contrat, la seule donnee d'entree publiee). La liste
 * d'exemption se calcule sur les entrees, JAMAIS sur la reponse : sinon une
 * reponse qui publie un mot de passe en guise d'`origin` s'exempterait
 * elle-meme, et le garde-fou ne garderait plus rien.
 */
export function assertNoCredentialOnBus(
  response: CredentialsAuditResponse,
  entrees: readonly VaultItem[],
  emailCompte = '',
): void {
  const serialise = JSON.stringify(response);
  if (emailCompte.trim() !== '' && serialise.includes(emailCompte.trim())) {
    throw new CredentialsAuditLeakError("credentials.audit: l'e-mail du compte allait partir sur le bus");
  }
  const origines = new Set(entrees.flatMap(originsOf));
  for (const entry of entrees) {
    for (const valeur of secretStrings(entry)) {
      if (valeur.length < 4 || origines.has(valeur.toLowerCase())) continue;
      if (serialise.includes(valeur)) {
        throw new CredentialsAuditLeakError(
          `credentials.audit: un secret du coffre (entree ${entry.id}) allait partir sur le bus`,
        );
      }
    }
  }
}

/**
 * Point d'entree de la capacite. Chemin UNIQUE : tout consommateur du bus
 * passe par ici, donc le garde-fou anti-fuite couvre tout le monde.
 */
export function serveCredentialsAudit(
  request: CredentialsAuditRequest,
  source: CredentialsAuditSource,
): CredentialsAuditResponse {
  const capacite = CREDENTIALS_AUDIT_CAPABILITY;
  // Regle 3 : « coffre non connecte » est un etat normal, pas une erreur.
  if (!source.deverrouille) return { capacite, etat: 'coffre-non-connecte' };
  // Regle 2 : NAVKRYPT demande l'affichage, il ne declenche pas un audit.
  if (!source.consenti) return { capacite, etat: 'consentement-requis' };

  // Une page sur `www.banque.fr` doit voir le verdict de l'entree enregistree
  // sur `connexion.banque.fr` : la frontiere est le SITE (domaine
  // enregistrable), jamais l'hote exact — ni « le dernier point », qui
  // confondrait `a.github.io` et `b.github.io`. S'y ajoutent les domaines
  // equivalents sources (`youtube.com` ↔ `accounts.google.com`), sauf pour une
  // entree qui s'en est retiree. Chaque adresse suit sa propre regle
  // (`secrets.uriMatch` : hote, prefixe, exacte, jamais...), a defaut celle du
  // coffre : seules les adresses qui reconnaissent la page lui envoient leur verdict.
  const demandees = request.origines;
  const reconnues =
    demandees === undefined
      ? null
      : new Set(
          source.entrees.flatMap((entry) =>
            hostsOf(demandees.flatMap((origine) => entryUrisMatchingSite(entry.secrets, origine, source.detectionParDefaut))),
          ),
        );
  const verdicts = buildCredentialsAudit(source).filter((verdict) => reconnues === null || reconnues.has(verdict.origin));
  const response: CredentialsAuditResponse = { capacite, etat: 'ok', verdicts };
  assertNoCredentialOnBus(response, source.entrees, source.emailCompte);
  return response;
}
